// Panel de detalle de una promo de venta sobre el chasis común del editor. Crear y editar pasan aquí;
// editar guarda una versión nueva.
import { useEffect } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { FormProvider, useForm, useWatch, type FieldErrors } from 'react-hook-form';
import { toast } from 'sonner';
import {
    LifecycleActions,
    LifecycleEditorShell,
    LifecycleNotice,
    LifecycleEditorLayout,
    LIFECYCLE_FIELD_HELP,
} from '@/shared/patterns/Lifecycle';
import { ValidityCalendars } from '@/shared/patterns/ValidityCalendars';
import type { PromotionFormSources } from '../api/usePromotionFormSources';
import type { SalePromotionListItem, SalePromotionTransition } from '../api/promotionsContract';
import { listStatusOf } from '../lib/promotionsList';
import { formatValidity } from '../lib/promotionsRow';
import { salePromotionFormSchema, type SalePromotionFormValues } from '../lib/salePromotionForm';
import { addDaysToDay } from '../lib/salePromotionDraft';
import { EditorField } from '@/shared/patterns/EditorForm';
import type { RetailerScopeColumnProps } from '@/shared/patterns/ScopeChips';
import { FIELD_INPUT, FieldMessage } from './editorParts';
import { promotionLifecycleActions } from './promotionLifecycle';
import { MULTI_RETAILER_COPY } from '../constants/helpText';
import { PromotionLogPanel } from './PromotionLogPanel';
import { SalePromotionBudgetEditor, SalePromotionRulesEditor } from './SalePromotionBudgetEditor';
import { SalePromotionLeversEditor } from './SalePromotionLeversEditor';
import { SalePromotionScopeEditor, SalePromotionScopeSummary } from './SalePromotionScopeEditor';
import {
    SalePromotionSegmentationEditor,
    SalePromotionSegmentationSummary,
} from './SalePromotionSegmentationEditor';

export interface SalePromotionEditorProps {
    /** `null` mientras se redacta una promo nueva. */
    promotion: SalePromotionListItem | null;
    defaultValues: SalePromotionFormValues;
    sources: PromotionFormSources;
    /** La columna «Cadena» del Alcance. */
    retailer?: RetailerScopeColumnProps;
    /** Ya hay copias creadas: la forma se congela para que el reintento mande lo mismo. */
    locked?: boolean;
    /** Aviso informativo sobre la promo nueva, como el SKU que llega de Inventario. */
    notice?: string;
    /** «Marca modelo» por SKU, para el resumen de palancas. */
    productNames: ReadonlyMap<string, string>;
    now: Date;
    /** Día CDMX `YYYY-MM-DD` contra el que se pintan los calendarios. */
    today: string;
    editable: boolean;
    canReadLog: boolean;
    saving: boolean;
    /** Resuelve `true` si guardó; entonces lo tecleado pasa a ser lo guardado. */
    onSave: (values: SalePromotionFormValues) => Promise<boolean>;
    /** Descartar una promo nueva cierra el panel: no hay versión a la que volver. */
    onCloseNew: () => void;
    onDirtyChange: (dirty: boolean) => void;
    onAction: (transition: SalePromotionTransition) => void;
}

/** El primer mensaje de error del formulario, en el orden en que se declararon los campos. */
function firstErrorMessage(errors: FieldErrors): string | undefined {
    for (const value of Object.values(errors)) {
        if (!value) continue;
        if (typeof value.message === 'string' && value.message) return value.message;
        const nested = firstErrorMessage(value as FieldErrors);
        if (nested) return nested;
    }
    return undefined;
}

export function SalePromotionEditor({
    promotion,
    defaultValues,
    sources,
    retailer,
    locked = false,
    notice,
    productNames,
    now,
    today,
    editable,
    canReadLog,
    saving,
    onSave,
    onCloseNew,
    onDirtyChange,
    onAction,
}: SalePromotionEditorProps) {
    const methods = useForm<SalePromotionFormValues>({
        defaultValues,
        resolver: zodResolver(salePromotionFormSchema),
    });
    const {
        control,
        register,
        reset,
        setValue,
        handleSubmit,
        formState: { errors, isDirty },
    } = methods;
    const isNew = promotion === null;
    const showSave = editable && (isNew || isDirty);

    useEffect(() => {
        onDirtyChange(isNew || isDirty);
    }, [isDirty, isNew, onDirtyChange]);

    const submit = handleSubmit(
        async (values) => {
            if (await onSave(values)) reset(values);
        },
        (formErrors) => {
            toast.error('Faltan datos para guardar', {
                description: firstErrorMessage(formErrors),
            });
        },
    );

    const discard = () => {
        if (isNew) onCloseNew();
        else reset(defaultValues);
    };

    const name = useWatch({ control, name: 'name' });
    const multiChain = retailer?.mode === 'choose' && retailer.selected.length > 1;
    // Al tocar una cadena se ajusta lo que depende de ella; ver onRetailerToggle.
    const retailerColumn: RetailerScopeColumnProps | undefined =
        retailer?.mode === 'choose'
            ? {
                  ...retailer,
                  onToggle: (retailerId) => {
                      onRetailerToggle(retailer.selected, retailerId);
                      retailer.onToggle(retailerId);
                  },
              }
            : retailer;

    return (
        <FormProvider {...methods}>
            <LifecycleEditorShell
                ariaLabel={isNew ? 'Promo nueva' : `Promo ${promotion.name}`}
                title={name.trim() || (isNew ? 'Promo nueva' : promotion.name)}
                dirty={promotion !== null && isDirty}
                subtitle={
                    isNew
                        ? 'Borrador nuevo · publicarla es un paso aparte'
                        : `Versión ${String(promotion.version)} · ${formatValidity(promotion)}`
                }
                status={promotion ? listStatusOf(promotion, now) : null}
                id={promotion ? { label: 'ID', value: promotion.promotionId } : undefined}
                actions={
                    promotion ? (
                        <LifecycleActions
                            actions={promotionLifecycleActions(promotion, now, onAction)}
                            dirty={isDirty}
                            menuLabel="Acciones de la promo"
                        />
                    ) : null
                }
                notices={renderNotices()}
                history={
                    promotion && canReadLog ? (
                        <PromotionLogPanel
                            promotionId={promotion.promotionId}
                            retailerId={promotion.retailerId}
                            compact
                        />
                    ) : null
                }
                saveBar={{
                    count: showSave ? 1 : 0,
                    message: isNew ? 'Promo nueva sin guardar' : 'Cambios sin guardar',
                    saving,
                    onDiscard: discard,
                    onSave: () => {
                        void submit();
                    },
                }}
            >
                <fieldset disabled={!editable || locked} className="min-w-0">
                    <LifecycleEditorLayout
                        description={
                            <>
                                <EditorField
                                    label="Nombre"
                                    help={LIFECYCLE_FIELD_HELP.name}
                                    error={errors.name?.message}
                                    className="mb-2.5"
                                >
                                    {(id) => (
                                        <input
                                            id={id}
                                            type="text"
                                            placeholder="ej. iPhone 15 · combo lanzamiento"
                                            aria-invalid={errors.name ? true : undefined}
                                            className={FIELD_INPUT}
                                            {...register('name')}
                                        />
                                    )}
                                </EditorField>
                                <EditorField
                                    label="Descripción"
                                    help={LIFECYCLE_FIELD_HELP.description}
                                >
                                    {(id) => (
                                        <textarea
                                            id={id}
                                            rows={6}
                                            placeholder="Cómo se le explica al cliente el beneficio"
                                            className={FIELD_INPUT}
                                            {...register('customerText')}
                                        />
                                    )}
                                </EditorField>
                            </>
                        }
                        levers={
                            <SalePromotionLeversEditor
                                productNames={productNames}
                                products={sources.products}
                                productsState={sources.productsState}
                                catalogNotice={
                                    multiChain ? MULTI_RETAILER_COPY.giftList : undefined
                                }
                            />
                        }
                        audience={
                            <SalePromotionSegmentationEditor
                                segments={sources.segments}
                                segmentsState={sources.segmentsState}
                            />
                        }
                        scope={
                            <SalePromotionScopeEditor
                                retailer={retailerColumn}
                                multiChain={multiChain}
                                products={sources.products}
                                productsState={sources.productsState}
                                stores={sources.stores}
                                storesState={sources.storesState}
                            />
                        }
                        summaries={{
                            audience: (
                                <SalePromotionSegmentationSummary segments={sources.segments} />
                            ),
                            scope: <SalePromotionScopeSummary />,
                        }}
                        rules={<SalePromotionRulesEditor />}
                        budget={
                            <SalePromotionBudgetEditor
                                consumedCents={promotion ? promotion.budgetConsumedCents : null}
                            />
                        }
                        validity={
                            <>
                                <ValidityField today={today} setValue={setValue} />
                                <FieldMessage message={errors.validUntil?.message} />
                            </>
                        }
                    />
                </fieldset>
            </LifecycleEditorShell>
        </FormProvider>
    );

    /** Lo elegido sin cadena se conserva; cambiar de cadena suelta lo que era de la anterior. */
    function onRetailerToggle(selected: readonly string[], retailerId: string) {
        const next = selected.includes(retailerId)
            ? selected.filter((one) => one !== retailerId)
            : [...selected, retailerId];
        if (selected.length > 0) {
            setValue('skus', [], { shouldDirty: true });
            setValue('storeIds', [], { shouldDirty: true });
            setValue('gift.skus', [], { shouldDirty: true });
        }
        // La lista de regalos es de una cadena: con varias, el regalo va por gama.
        if (next.length > 1 && methods.getValues('gift.mode') === 'LIST')
            setValue('gift.mode', 'RANGE', { shouldDirty: true });
    }

    function renderNotices() {
        if (notice && isNew && !locked) {
            return (
                <LifecycleNotice tone="info" role="note">
                    {notice}
                </LifecycleNotice>
            );
        }
        if (locked) {
            return (
                <LifecycleNotice tone="info" role="note">
                    {MULTI_RETAILER_COPY.locked}
                </LifecycleNotice>
            );
        }
        if (promotion && !editable) {
            return (
                <LifecycleNotice>
                    Esta promo ya no se edita: está vencida o archivada, o no tienes permiso de
                    editar.
                </LifecycleNotice>
            );
        }
        if (promotion && isDirty) {
            return (
                <LifecycleNotice tone="info" role="note">
                    Guardar crea la versión {promotion.version + 1} para las ventas futuras. Las
                    ventas ya cotizadas conservan su versión. Para cambiar el estado, guarda o
                    descarta primero.
                </LifecycleNotice>
            );
        }
        return null;
    }
}

/** Vigencia con los calendarios del portal: ∞ Permanente, Desde y Hasta. */
function ValidityField({
    today,
    setValue,
}: {
    today: string;
    setValue: ReturnType<typeof useForm<SalePromotionFormValues>>['setValue'];
}) {
    const [validFrom, permanent, validUntil] = useWatch<
        SalePromotionFormValues,
        ['validFrom', 'permanent', 'validUntil']
    >({ name: ['validFrom', 'permanent', 'validUntil'] });
    const dirty = { shouldDirty: true, shouldValidate: false } as const;
    return (
        <ValidityCalendars
            validFrom={validFrom === '' ? null : validFrom}
            validTo={permanent || validUntil === '' ? null : validUntil}
            today={today}
            onChange={({ validFrom: from, validTo }) => {
                setValue('validFrom', from ?? '', dirty);
                setValue('validUntil', validTo ?? '', dirty);
                if (validTo) setValue('permanent', false, dirty);
            }}
            onTogglePermanent={(isPermanent) => {
                if (isPermanent) {
                    setValue('permanent', false, dirty);
                    setValue('validUntil', addDaysToDay(validFrom || today, 14), dirty);
                } else {
                    setValue('permanent', true, dirty);
                    setValue('validUntil', '', dirty);
                }
            }}
        />
    );
}
